import { Capacitor } from '@capacitor/core';
import { ximiUserManager } from '../../utilities/ximi/oidcConfig';
import { endXimiBrowserSsoSession } from '../../utilities/ximi/openXimiAuthBrowser';

/**
 * Logout SSO Ximi, then clears the local oidc user.
 * No-op without an oidc session (classic email/password user).
 * - Web: end_session redirect (clears the Xelya.Auth cookie, no silent sign-in back). The redirect
 *   leaves the page (signoutRedirect only resolves on a back-forward cache return), so the caller's
 *   clean-up after it never ran: `clearLocalSessionBeforeRedirect` runs first. Back on the app, a
 *   Stimul'In token left without its SSO proof answered 401 → `/login?ximiError=1`.
 * - Native: refresh-token revocation, then end of the system-browser SSO session
 *   (Android Custom Tab; nothing to end on iOS, signed in through an ephemeral session).
 */
export async function signOutXimiSso(
    clearLocalSessionBeforeRedirect: () => Promise<void>
): Promise<void> {
    const user = await ximiUserManager.getUser();
    if (!user) return;

    if (!Capacitor.isNativePlatform()) {
        await clearLocalSessionBeforeRedirect();
        // The local clean-up purged the oidc user too: the hint is passed explicitly.
        await ximiUserManager.signoutRedirect({ id_token_hint: user.id_token });
        return;
    }

    try {
        await ximiUserManager.revokeTokens(['refresh_token']);
        await endXimiBrowserSsoSession(user.id_token);
    } catch {
        // best-effort: offline or refused by the AuthProvider must not block the logout
    }
    await ximiUserManager.removeUser();
}
